import type { GaVueComponent } from "@/common/vueUtils";
import { Filter } from "@/components/sorting-filtering-paging/Filter";
import { GaButton } from "@/components/general/GaButton";
import type { Store } from "@/common/storeUtils";
import { GaChip } from "@/components/general/GaChip";
import { FilterChip } from "@/components/sorting-filtering-paging/FilterChip";
import { useGaContext } from "@utils/vue-migration/common/gaContext/gaContext";
import type { ActionableFiltersForm, FormFilter, MiabFiltersForm, SummaryFiltersForm } from "@newgenerated/shared/schema";
import type { MultiSelectStateProps } from "@/components/form/GaFormFieldMultiSelect";
import type { DeepReadonly } from "vue";
import { assert } from "@utils/assertion";
import { Language } from "@utils/type/type";
import { getActiveFilterValues } from "@/components/search/fullSearchStore";

export type FormFilterExtended = FormFilter & {
  isCollapsed: boolean;
  multiSelectProps: MultiSelectStateProps<string> | null;
};

export type ContentTypeFilterProps = {
  formFilters: FormFilterExtended[];
  formFilterExtended: boolean;
};

function getMultiSelectProps(formFilter: FormFilter): MultiSelectStateProps<string> | null {
  if (formFilter.type === "MULTISELECT") {
    return {
      searchTerm: "",
      options: formFilter.options,
      showSearch: false,
    };
  }
  return null;
}

export const toFormFilterExtended = (formFilters: FormFilter[]): FormFilterExtended[] => {
  return formFilters.map((filter) => ({
    ...filter,
    isCollapsed: true,
    multiSelectProps: getMultiSelectProps(filter),
  }));
};

function convertToNumbers(input: string[]): number[] {
  return input.map((value) => {
    const number = parseInt(value);
    if (Number.isNaN(number)) {
      throw Error("could not parse number");
    }
    return number;
  });
}

function convertToLanguages(values: string[]): Language[] {
  return values.map((value) => {
    assert(Language.getValues().includes(value as Language));
    return value as Language;
  });
}

export const convertToSummaryFiltersForm = (filters: DeepReadonly<FormFilterExtended[]>): SummaryFiltersForm => {
  const audioFilter = getActiveFilterValues(filters, "AUDIO").at(0);
  const miabFilter = getActiveFilterValues(filters, "MIAB").at(0);
  assert(audioFilter === "true" || audioFilter === "false" || audioFilter === undefined);
  assert(miabFilter === "true" || miabFilter === "false" || miabFilter === undefined);
  return {
    qualityFormFilter: [...getActiveFilterValues(filters, "QUALITY")],
    ratingFormFilter: convertToNumbers([...getActiveFilterValues(filters, "RATING")]),
    sourceFormFilter: [...getActiveFilterValues(filters, "SOURCE")],
    languageFormFilter: [...getActiveFilterValues(filters, "LANGUAGE")].map((language) => Language.getLangByIsoCode(language)),
    audioFormFilter: audioFilter === "true",
    publicationDateFormFilter: convertToNumbers([...getActiveFilterValues(filters, "PUBLICATION_DATE")]),
    miabFormFilter: miabFilter === "true",
  };
};

export const convertToActionableFiltersForm = (filters: DeepReadonly<FormFilterExtended[]>): ActionableFiltersForm => {
  return { languageFormFilter: convertToLanguages([...getActiveFilterValues(filters, "LANGUAGE")]) };
};

export const convertToMiabFiltersForm = (filters: DeepReadonly<FormFilterExtended[]>): MiabFiltersForm => {
  return { languageFormFilter: convertToLanguages([...getActiveFilterValues(filters, "LANGUAGE")]) };
};

export function ContentFilters(props: { filterStore: Store<ContentTypeFilterProps>; applyFilters: () => void }): GaVueComponent {
  const { t } = useGaContext();
  const formFilterStore = props.filterStore.sub("formFilters");
  return (
    <>
      <div class="d-flex flex-wrap gap-2 mb-3">
        <GaChip icon="ico-equalizer" type={"checkbox"} changeHandler={() => props.filterStore.sub("formFilterExtended").set(!props.filterStore.sub("formFilterExtended").get())}>
          {t("summary:filtersTitle")}
        </GaChip>
        {formFilterStore
          .get()
          .filter((filter) => filter.activeValues.length > 0)
          .map((filter) => formFilterStore.sub(formFilterStore.get().findIndex((f) => f.identifier === filter.identifier)))
          .map((filter) => (
            <FilterChip filter={filter} />
          ))}
      </div>
      <div class={["collapse", props.filterStore.sub("formFilterExtended").get() ? "show" : ""]}>
        <div>
          <div class="filtering-dropdown">
            <div class="filtering-dropdown__left-col">
              {formFilterStore
                .get()
                .filter((filter) => filter.priority === "PRIMARY")
                .map((filter) => formFilterStore.sub(formFilterStore.get().findIndex((f) => f.identifier === filter.identifier)))
                .map((filter) => (
                  <Filter filter={filter} />
                ))}
            </div>
            <div class="filtering-dropdown__right-col">
              {formFilterStore
                .get()
                .filter((filter) => filter.priority === "SECONDARY")
                .map((filter) => formFilterStore.sub(formFilterStore.get().findIndex((f) => f.identifier === filter.identifier)))
                .map((filter) => (
                  <Filter filter={filter} />
                ))}
            </div>
            <div>
              <GaButton onClick={props.applyFilters}>{t("general:button.apply")}</GaButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
